// Shared insurance-policy presentation helpers. Status is derived from the
// start/end dates the policy already stores (no schema change), and reused by
// the policies page, the assignment page, and the dashboard expiry reminder.

export const POLICY_STATUS_META = {
  active: { label: 'Active', tone: 'success' },
  expiring: { label: 'Expiring soon', tone: 'warning' },
  expired: { label: 'Expired', tone: 'secondary' },
};

function toDay(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// active = no end date or ends in > 30 days; expiring = ends within 30 days;
// expired = end date in the past.
export function getPolicyStatus(policy, refDate = new Date()) {
  const end = toDay(policy?.end_date);
  if (!end) return 'active';
  const today = toDay(refDate) || new Date();
  const diffDays = Math.round((end - today) / 86400000);
  if (diffDays < 0) return 'expired';
  if (diffDays <= 30) return 'expiring';
  return 'active';
}

export function daysUntil(value) {
  const target = toDay(value);
  if (!target) return null;
  const today = toDay(new Date());
  return Math.round((target - today) / 86400000);
}

// Short human label for the date-relative hint shown next to the range.
export function expiryHint(policy) {
  const status = getPolicyStatus(policy);
  const d = daysUntil(policy?.end_date);
  if (d == null) return null;
  if (status === 'expired') return `Ended ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} ago`;
  if (status === 'expiring') return d === 0 ? 'Ends today' : `${d} day${d === 1 ? '' : 's'} left`;
  return null;
}

export function formatCoverage(amount, currency = 'SAR') {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '-';
  return `${currency} ${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

export function formatPolicyDate(value) {
  const d = toDay(value);
  if (!d) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function policyDateRange(policy) {
  const start = formatPolicyDate(policy?.start_date);
  const end = formatPolicyDate(policy?.end_date);
  if (start && end) return `${start} – ${end}`;
  if (start) return `From ${start}`;
  if (end) return `Until ${end}`;
  return 'No dates set';
}

export function asClassifications(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}
